import { DatasetExploreItem } from "./dataset";
import {
  CoverageConfigType,
  ECatchmentTransport,
  ERoadExclusions,
  ScoringOptionsEnum
} from "./spatial";

export interface GetAnalysisHistoryParams {
  page?: number;
  per_page?: number;
  query?: string;
  author_id?: string;
  created_at_from?: string;
  created_at_to?: string;
}

interface AnalysisHistoryDataset {
  id: string;
  name: string;
}

export type PolygonUI = {
  name: string;
  coordinates: string;
};

export interface PayloadAttributes {
  attributes?: DatasetAttribute[];
}

export interface AnalysisHistoryItem {
  id: string;
  created_at: string;
  author: string;
  analysis_name: string;
  datasets: AnalysisHistoryDataset[];
  input: string;
  output: string;
  expired_at: string;
  estimated_time_secs: number;
  conversation_id?: string;
}

interface Pagination {
  current_page?: number;
  total_pages?: number;
  per_page: number;
  total: number;
  prev: number;
  next: number;
}

export type AnalysisHistoryListResponse = {
  data: AnalysisHistoryItem[];
  meta?: {
    pagination: Pagination;
  };
};

export enum OutputType {
  SITE_PROFILING = "site_profiling",
  H3 = "h3",
  GEOHASH = "geohash",
  GRID = "grid"
}

enum CatchmentMetric {
  DISTANCE = "distance",
  TIME = "time"
}

interface AnalysisOutput extends CoverageConfigType {
  level: number;
  output_type: OutputType;
  scoring_option: ScoringOptionsEnum;
}

interface AttributeFilter {
  criteria: string;
  include_nulls: boolean;
  type: string;
}

export interface DatasetAttribute {
  attribute_code: string;
  filter: AttributeFilter;
  id: string;
  name: string;
  weight_score: number;
}

interface CatchmentLocation {
  catchment_area: number | null;
  latitude: number;
  longitude: number;
  name: string;
}

interface Catchment {
  contours: number[];
  exclusions: ERoadExclusions[];
  locations: CatchmentLocation[];
  metric: CatchmentMetric;
  profile: ECatchmentTransport | "radius";
}

export interface AdmAreaInput {
  type: "adm_area";
  adm_area: string[][];
}

export interface CatchmentAreaInput {
  type: "catchment";
  catchment: Catchment;
}
export interface PolygonFeature {
  type: "Feature";
  geometry: {
    type: "Polygon" | "MultiPolygon";
    coordinates: string | number[][][] | number[][][][];
  };
  properties: {
    name?: string;
    area_size?: number;
  };
}

export interface PolygonAreaInput {
  type: "custom_catchments";
  custom_catchments: {
    features: PolygonFeature[];
  };
}

export type AreaInput = AdmAreaInput | CatchmentAreaInput | PolygonAreaInput;

export interface DatasetHistoryAnalysisItem extends DatasetExploreItem {
  short_desc?: string;
  attributes?: DatasetAttribute[];
  region?: string;
  logo_asset_url?: string;
}

export interface AnalysisHistoryDetailResponse {
  analysis_output: AnalysisOutput;
  area_input: AreaInput;
  author_name: string;
  created_at: string;
  datasets: DatasetHistoryAnalysisItem[];
  expired_at: string;
  id: string;
  /** Set when the analysis was produced by an AI Analyst conversation. */
  conversation_id?: string;
  conversation_title?: string;
}

export enum GeohashResolution {
  RES_1 = 1,
  RES_2 = 2,
  RES_3 = 3,
  RES_4 = 4,
  RES_5 = 5,
  RES_6 = 6
}

export enum H3Resolution {
  LEVEL_1 = 1,
  LEVEL_2 = 2,
  LEVEL_3 = 3,
  LEVEL_4 = 4,
  LEVEL_5 = 5,
  LEVEL_6 = 6,
  LEVEL_7 = 7,
  LEVEL_8 = 8,
  LEVEL_9 = 9,
  LEVEL_10 = 10
}

export enum AnalysisHistoryInputType {
  CATCHMENT = "catchment",
  ADMIN = "adm_area",
  POLYGON = "custom_catchments"
}
